import { ScrollArea } from "@/components/ui/scroll-area";
import { useMemo } from "react";
import type { GetConversationByIdResponse } from "@/redux/api/conversations_new.rtkq";
import type { Answer } from "@/components/types";
import ConversationList from "../messages/ConversationList";
import { MessageCircle } from "lucide-react";
import SendQueryToChat from "../utils/SendQueryToChat";

interface ConversationMainAreaProps {
  conversationData: GetConversationByIdResponse;
  onDocumentClick: (documentId: string) => void;
}

const ConversationMainArea = ({ conversationData, onDocumentClick }: ConversationMainAreaProps) => {
  // Extract answer and source documents from chat history
  const answer = useMemo(() => {
    if (!conversationData?.chat_history) return null;

    // Find the assistant message with answer content
    const assistantMessage = conversationData.chat_history.find(
      (msg) => msg.role === "assistant" && typeof msg.content === "object"
    );

    return (assistantMessage?.content as Answer) || null;
  }, [conversationData]);

  useMemo(() => {
    if (!conversationData?.chat_history) return [];

    // Find the assistant message with source documents
    const assistantMessage = conversationData.chat_history.find((msg) => msg.role === "assistant" && "source_documents" in msg);

    return assistantMessage?.source_documents || [];
  }, [conversationData]);

  return (
    <ScrollArea className="flex-1 h-full">
      <div className="p-8 max-md:p-6 max-sm:p-4 mb-32" dir="rtl">
        {/* Answer sections */}
        {answer && answer.sections && Array.isArray(answer.sections) ? (
          <ConversationList
            messages={[{ userQuestion: conversationData.chat_history[0].content as string, assistantAnswer: answer }]}
            onDocumentClick={onDocumentClick}
            isInitialMessage={true}
          />
        ) : (
          <div className="text-center py-12 text-slate-500">
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">אין תוכן תשובה זמין</h3>
                <p className="text-sm">התשובה לא מכילה קטעים זמינים או שיש בעיה בטעינת הנתונים</p>
              </div>
            </div>
          </div>
        )}

        {/* Followup messages */}
        <ConversationList
          chatHistory={conversationData.chat_history}
          onDocumentClick={onDocumentClick}
          isFollowupMessages={true}
        />
      </div>

      {/* Input component */}
      <SendQueryToChat isFollowupQuery={true} chatHistory={conversationData.chat_history} />
    </ScrollArea>
  );
};

export default ConversationMainArea;
